import axios from "axios";
import { toast } from "sonner";
import { cacheGet, cacheSet, cacheKey, enqueue, isAdminContext, isNetworkError, matchRule, setOffline, syncQueue, refreshState } from "@/lib/offline";
import { optimizeImage } from "@/lib/image";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const fileUrl = (p) => (!p ? undefined : /^(https?:|data:)/.test(p) ? p : `${API}/files/${p}`);

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// ---------- Modo offline (só painel Admin) ----------
const _realAdapter = axios.getAdapter(axios.defaults.adapter);
const NO_CACHE = /^\/(auth\/(login|admin-login|logout)|school\/)/;
const offlineError = () => {
  const e = new Error("offline");
  e.response = { status: 503, data: { detail: "Sem internet: esta ação precisa de conexão." } };
  return e;
};
api.defaults.adapter = async (cfg) => {
  const method = (cfg.method || "get").toLowerCase();
  const admin = isAdminContext() && !cfg.noOffline;
  try {
    const res = await _realAdapter(cfg);
    if (admin) {
      setOffline(false);
      if (method === "get" && !NO_CACHE.test(cfg.url) && res.status < 300) {
        let d = res.data;
        if (typeof d === "string") { try { d = JSON.parse(d); } catch { /* mant\u00e9m texto */ } }
        cacheSet(cacheKey(cfg), { data: d, savedAt: new Date().toISOString() }).catch(() => {});
      }
    }
    return res;
  } catch (err) {
    if (!admin || !isNetworkError(err)) throw err;
    if (method === "get") {
      const c = await cacheGet(cacheKey(cfg)).catch(() => null);
      setOffline(true, c?.savedAt);
      if (c) return { data: c.data, status: 200, statusText: "OK (offline)", headers: {}, config: cfg, request: null, offline: true };
      throw err;
    }
    setOffline(true);
    const rule = matchRule(cfg);
    if (!rule) throw offlineError();
    const data = await enqueue(cfg, rule);
    toast.info("Sem internet: salvo no celular. Será enviado quando a conexão voltar.");
    return { data, status: 202, statusText: "Queued (offline)", headers: {}, config: cfg, request: null, offline: true };
  }
};

export const syncOfflineQueue = () => syncQueue((c) => api.request({ ...c, noOffline: true, silent: true }));
if (typeof window !== "undefined") {
  window.addEventListener("online", () => { setOffline(false); syncOfflineQueue(); });
  window.addEventListener("offline", () => setOffline(true));
  refreshState().then(() => syncOfflineQueue()).catch(() => {});
  setInterval(() => { if (navigator.onLine) syncOfflineQueue(); }, 30000);
}

// ---------- Global loading tracker ----------
// A tiny pub/sub around axios interceptors so a top progress bar can react.
let _pending = 0;
const _listeners = new Set();
const _notify = () => _listeners.forEach((fn) => fn(_pending));

// ---------- Notify-school toggle ----------
// Pages that mutate a budget (BudgetDetail) can flip this OFF to tell
// the backend NOT to send push/bell to the school for a given session.
// Reset to true on unmount so other pages inherit the normal behavior.
let _notifySchool = true;
export const setNotifySchool = (v) => { _notifySchool = v !== false; };
export const getNotifySchool = () => _notifySchool;

export const subscribeLoading = (fn) => {
  _listeners.add(fn);
  fn(_pending);
  return () => _listeners.delete(fn);
};
api.interceptors.request.use((cfg) => {
  // Opt-out per-request via { silent: true } in config
  if (!cfg.silent) {
    _pending += 1;
    _notify();
  }
  // Attach the "Avisar escola?" preference (toggle lives in BudgetDetail
  // and is reset on unmount).  Default is omit the header → backend
  // notifies as before.
  if (_notifySchool === false) {
    cfg.headers = cfg.headers || {};
    cfg.headers["X-Notify-School"] = "0";
  }
  return cfg;
});
const _finish = (cfg) => {
  if (cfg && !cfg.silent) {
    _pending = Math.max(0, _pending - 1);
    _notify();
  }
};
api.interceptors.response.use(
  (res) => { _finish(res.config); return res; },
  (err) => {
    _finish(err?.config);
    if (err?.response?.status === 423) window.dispatchEvent(new Event("admin-locked"));
    throw err;
  }
);

api.interceptors.request.use(async (cfg) => {
  // Otimiza fotos automaticamente antes de enviar (/upload)
  if (["/upload", "/landing/media"].includes(cfg.url) && cfg.data instanceof FormData && !cfg.noOffline) {
    const f = cfg.data.get("file");
    if (f instanceof Blob) {
      const opt = await optimizeImage(f);
      if (opt !== f) cfg.data.set("file", opt, opt.name);
    }
  }
  return cfg;
});

// Unit value to display: with an item discount, show the already-discounted unit.
export const shownUnit = (it) => {
  const q = Number(it?.quantity) || 0;
  return Number(it?.discount) > 0 && q > 0 ? Number(it.total || 0) / q : Number(it?.unit_value || 0);
};


export const BRL = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

// Compact BRL for narrow mobile cards: R$ 48,3 mil / R$ 1,2 mi
export const BRL_COMPACT = (v) => {
  const n = Number(v || 0);
  const abs = Math.abs(n);
  if (abs >= 1_000_000) {
    return `R$ ${(n / 1_000_000).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 2 })} mi`;
  }
  if (abs >= 10_000) {
    return `R$ ${(n / 1_000).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} mil`;
  }
  if (abs >= 1_000) {
    return `R$ ${(n / 1_000).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} mil`;
  }
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

export const formatDateBR = (iso) => {
  if (!iso) return "--/--/----";
  try {
    const d = new Date(iso + (iso.length === 10 ? "T00:00:00" : ""));
    return d.toLocaleDateString("pt-BR");
  } catch (e) {
    return iso;
  }
};

export const STATUS_LABELS = {
  aguardando: "Aguardando",
  aprovado: "Aprovado",
  reprovado: "Reprovado",
  iniciando: "Iniciado",
  finalizado: "Finalizado",
  nota_fiscal_gerada: "Nota Fiscal Gerada",
  nota_faturada: "Nota Fiscal Paga",
  deletado: "Deletado",
  ex_sistema: "Ex Sistema",
  validade_expirada: "Validade Expirada",
};

export const STATUS_ORDER = [
  "aguardando",
  "aprovado",
  "iniciando",
  "finalizado",
  "nota_fiscal_gerada",
  "nota_faturada",
];

// Item changes require a reason only after the school approved the budget.
export const REASON_REQUIRED_STATUSES = ["aprovado", "iniciando", "finalizado", "nota_fiscal_gerada", "nota_faturada"];
export const reasonRequired = (status) => REASON_REQUIRED_STATUSES.includes(status);

export const STATUS_COLORS = {
  aguardando: "bg-orange-100 text-orange-700 border-orange-200",
  aprovado: "bg-green-100 text-green-700 border-green-200",
  reprovado: "bg-red-100 text-red-700 border-red-200",
  iniciando: "bg-blue-100 text-blue-700 border-blue-200",
  finalizado: "bg-emerald-100 text-emerald-700 border-emerald-200",
  nota_fiscal_gerada: "bg-indigo-100 text-indigo-700 border-indigo-200",
  nota_faturada: "bg-violet-100 text-violet-700 border-violet-200",
  deletado: "bg-slate-200 text-slate-600 border-slate-300",
  ex_sistema: "bg-amber-50 text-amber-700 border-amber-200",
  validade_expirada: "bg-orange-100 text-orange-800 border-orange-300",
};

export const HERO_IMAGE =
  "https://customer-assets-lqy194kg.emergentagent.net/job_amc-orcamentos/artifacts/b97d16a11f149d83_2A339EA6-75DB-4247-ADE6-B1C7D9904670.png";
